import { createSelectors } from "@mod/core/utils/create-selectors";
import dayjs from "dayjs";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { GroupStore, GroupStoreActions } from "./types";

const initialState: GroupStore = {
  groupSelected: null,
  viewAllGroups: false,
  eventSelected: null,
  participantSelected: null,
  serverSelected: null,
  filterSelected: "all",
  mode: "guest",
  mainTabSelected: "chips",
  guestPanelIsOpen: false,
  groupEventFilterIsOpen: false,
  startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
  filtersSelected: {
    guest: true,
    server: false,
    confirmed: true,
    male: false,
    female: false,
    invited: false,
    pending: false,
    "next-time": false,
    declined: false,
    paid: false,
    "pending-payment": false,
    partial: false,
    diet: false,
    illness: false,
    vehicle: false,
    extraStay: false,
    extraFood: false,
    "no-attendance": false,
    attendance: false,
    under25: false,
    above25: false,
    notSingle: false,
    single: false
  },
  bottomOptionSelected: "participants"
};

const useGroupStoreBase = create(
  persist<GroupStore & GroupStoreActions>(
    (set, get) => ({
      ...initialState,
      selectGroup: (group) => {
        set(() => ({
          groupSelected: group || null
        }));
      },
      setViewAllGroups: (viewAllGroups) => {
        set(() => ({
          viewAllGroups
        }));
      },
      selectParticipant: (participant) =>
        set(() => ({
          participantSelected: participant || null
        })),
      selectEvent: (event) =>
        set(() => ({
          eventSelected: event || null
        })),
      selectServer: (server) => set(() => ({ serverSelected: server })),
      setMode: (mode) => set(() => ({ mode })),
      selectFilter: (filter) => set(() => ({ filterSelected: filter })),
      selectFilters: (filters) => set((prev) => ({ filtersSelected: { ...prev.filtersSelected, ...filters } })),
      selectBottomOption: (option) => set(() => ({ bottomOptionSelected: option })),
      isEventInProgress: () => !!get().eventSelected && !dayjs().isBefore(dayjs(get().eventSelected?.startDate), "day"),
      isEventDone: () =>
        !!get().eventSelected &&
        dayjs().isAfter(
          dayjs(get().eventSelected?.startDate).add((get().eventSelected?.durationDays ?? 2) - 1, "day"),
          "day"
        ) &&
        !get().eventSelected?.allowEditAfterDate,
      selectMainTab: (tab) => set(() => ({ mainTabSelected: tab })),
      setGuestPanelIsOpen: (isOpen) => set(() => ({ guestPanelIsOpen: isOpen })),
      setGroupEventFilterIsOpen: (isOpen) => set(() => ({ groupEventFilterIsOpen: isOpen })),
      setStartDate: (date) => set(() => ({ startDate: date })),
      reset: () => set(() => ({ ...initialState }))
    }),
    {
      name: "group-storage", // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage) // (optional) by default, 'localStorage' is used
    }
  )
);

export const useGroupStore = createSelectors(useGroupStoreBase);
